import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail, Calendar, Heart, Activity, FileText, MapPin } from 'lucide-react';

const PatientRecordDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();

  // Mock patient data - would come from API in production
  const mockPatientData = {
    '1': {
      id: '1',
      name: 'Mrs. Ngozi Okeke',
      mrn: 'MRN-20260130-AB123',
      email: 'ngozi.okeke@email.com',
      phone: '+234 803 456 7890',
      dateOfBirth: '1990-05-15',
      age: 36,
      address: '15 Ademola Street, Ikeja, Lagos',
      status: 'Active',
      patientType: 'Mother',
      condition: 'Prenatal Care',
      bloodGroup: 'O+',
      allergies: 'None',
      vitals: {
        weight: '68 kg',
        height: '165 cm',
        bloodPressure: '120/80 mmHg',
        temperature: '36.8°C',
        heartRate: '75 bpm',
        lastUpdated: '2026-01-28',
      },
      maternalHealth: {
        pregnancyStatus: 'Pregnant',
        gestationalAge: '24 weeks',
        lastMenstrualPeriod: '2025-08-01',
        expectedDueDate: '2026-05-08',
        fundalHeight: '24 cm',
      },
      medicalHistory: 'Previous pregnancy was normal delivery. No complications.',
      currentMedications: 'Prenatal vitamins, Folic acid 400mcg daily',
      visits: [
        { date: '2026-01-28', type: 'Antenatal Check-up', doctor: 'Dr. Sarah Johnson', notes: 'Normal progress, BP stable' },
        { date: '2026-01-15', type: 'Ultrasound', doctor: 'Dr. Michael Chen', notes: 'Baby development normal' },
        { date: '2026-01-01', type: 'General Check-up', doctor: 'Dr. Sarah Johnson', notes: 'All vitals normal' },
      ],
    },
    '2': {
      id: '2',
      name: 'Mrs. Fatima Bello',
      mrn: 'MRN-20260125-CD456',
      email: 'fatima.bello@email.com',
      phone: '+234 805 234 5678',
      dateOfBirth: '1988-11-22',
      age: 37,
      address: '42 Ogunlana Drive, Surulere, Lagos',
      status: 'Active',
      patientType: 'Mother',
      condition: 'Postnatal Care',
      bloodGroup: 'A+',
      allergies: 'Penicillin',
      vitals: {
        weight: '72 kg',
        height: '160 cm',
        bloodPressure: '115/75 mmHg',
        temperature: '37.0°C',
        heartRate: '72 bpm',
        lastUpdated: '2026-01-27',
      },
      maternalHealth: {
        pregnancyStatus: 'Postnatal',
        gestationalAge: 'Delivered',
        deliveryDate: '2026-01-10',
        deliveryType: 'Normal Delivery',
      },
      medicalHistory: 'Second child, first delivery was by C-section',
      currentMedications: 'Iron supplements, Postpartum vitamins',
      visits: [
        { date: '2026-01-27', type: 'Postnatal Follow-up', doctor: 'Dr. Sarah Johnson', notes: 'Recovery progressing well' },
        { date: '2026-01-20', type: 'Postnatal Check', doctor: 'Dr. Michael Chen', notes: 'No complications' },
      ],
    },
  };

  const patient = mockPatientData[patientId] || mockPatientData['1'];

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/nurse/patients')}
          className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Patients
        </button>

        {/* Patient Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <User className="w-12 h-12 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{patient.name}</h1>
                  <p className="text-gray-600 mt-1">MRN: {patient.mrn}</p>
                </div>
                <div className="flex gap-2">
                  <span className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold">
                    {patient.status}
                  </span>
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold">
                    {patient.patientType}
                  </span>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 text-emerald-600" />
                  <span>{patient.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 text-emerald-600" />
                  <span>{patient.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                  <span>Age: {patient.age} years</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span>{patient.address}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  <span>Blood Group: {patient.bloodGroup}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vital Signs */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Activity className="w-6 h-6 text-emerald-600" />
                Vital Signs
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-gray-600 mb-1">Weight</p>
                  <p className="text-2xl font-bold text-gray-900">{patient.vitals.weight}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1">Blood Pressure</p>
                  <p className="text-2xl font-bold text-gray-900">{patient.vitals.bloodPressure}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl border border-pink-200">
                  <p className="text-sm text-gray-600 mb-1">Heart Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{patient.vitals.heartRate}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200">
                  <p className="text-sm text-gray-600 mb-1">Height</p>
                  <p className="text-2xl font-bold text-gray-900">{patient.vitals.height}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-gray-600 mb-1">Temperature</p>
                  <p className="text-2xl font-bold text-gray-900">{patient.vitals.temperature}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Last updated: {patient.vitals.lastUpdated}</p>
            </div>

            {/* Maternal Health */}
            {patient.maternalHealth && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-600" />
                  Maternal Health
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Pregnancy Status</p>
                    <p className="text-gray-900">{patient.maternalHealth.pregnancyStatus}</p>
                  </div>
                  {patient.maternalHealth.gestationalAge && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Gestational Age</p>
                      <p className="text-gray-900">{patient.maternalHealth.gestationalAge}</p>
                    </div>
                  )}
                  {patient.maternalHealth.expectedDueDate && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Expected Due Date</p>
                      <p className="text-gray-900">{patient.maternalHealth.expectedDueDate}</p>
                    </div>
                  )}
                  {patient.maternalHealth.fundalHeight && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Fundal Height</p>
                      <p className="text-gray-900">{patient.maternalHealth.fundalHeight}</p>
                    </div>
                  )}
                  {patient.maternalHealth.deliveryDate && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Delivery Date</p>
                      <p className="text-gray-900">{patient.maternalHealth.deliveryDate}</p>
                    </div>
                  )}
                  {patient.maternalHealth.deliveryType && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">Delivery Type</p>
                      <p className="text-gray-900">{patient.maternalHealth.deliveryType}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Visit History */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-blue-600" />
                Visit History
              </h2>
              <div className="space-y-4">
                {patient.visits.map((visit, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{visit.type}</h3>
                        <p className="text-sm text-gray-600">Dr. {visit.doctor}</p>
                      </div>
                      <span className="text-sm text-gray-500">{visit.date}</span>
                    </div>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      <strong>Notes:</strong> {visit.notes}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Medical Information */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Medical Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Allergies</p>
                  <p className="text-gray-900">{patient.allergies}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Current Medications</p>
                  <p className="text-gray-900 text-sm">{patient.currentMedications}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Medical History</p>
                  <p className="text-gray-900 text-sm">{patient.medicalHistory}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Condition</p>
                  <p className="text-gray-900">{patient.condition}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/nurse/patient-records')}
                  className="w-full px-4 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all"
                >
                  Update Records
                </button>
                <button
                  onClick={() => navigate('/nurse/home-visits')}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
                >
                  Schedule Home Visit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientRecordDetail;
